import React, { FC, useState, useEffect } from 'react';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import Add from '@mui/icons-material/Add';

import DataTable from '../../../sharedModules/dataTable';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import axios from '../../../sharedModules/utils/axios.utils';

interface ExistingAssetsSectionProps {
  customerId: string;
  submissionId?: string;
  fetchCertificateSubmission?: () => void;
}

const ExistingAssetsSection: FC<ExistingAssetsSectionProps> = (props) => {
  let { customerId, submissionId, fetchCertificateSubmission } = props;

  let [assets, setAssets] = useState([]);
  let [errorMessage, setErrorMessage] = useState<string>('');
  let [expanded, setExpanded] = useState<boolean>(true);
  let [loading, setLoading] = useState<boolean>(false);
  let [submittingAssetId, setSubmittingAssetId] = useState<string | null>(null);

  const fetchAllCustomerAssets = async () => {
    setLoading(true);
    const response: any = await axios.get('/energy_solutions/customer_assets', {
      params: { customer_id: customerId },
    });
    setAssets(response.data.result);
    setLoading(false);
  };

  const handleSubmitAsset = async (assetId: string) => {
    if (!submissionId) {
      console.error('No submission ID provided');
      return;
    }

    setSubmittingAssetId(assetId);
    try {
      await axios.post(`/energy_solutions/certificate_submissions/${submissionId}/submit_asset`, { asset_id: assetId });
      fetchCertificateSubmission();
    } catch (error) {
      setErrorMessage('Something Went Wrong');
    } finally {
      setSubmittingAssetId(null);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchAllCustomerAssets();
    }
  }, [customerId]);

  const assetColumns = [
    {
      name: 'Asset',
      selector: 'asset_number',
      sortable: true,
      grow: 0.5,
    },
    {
      name: 'Manufacturer',
      selector: 'manufacturer_name',
      sortable: true,
      grow: 0.8,
    },
    {
      name: 'Model',
      selector: 'model_number',
      sortable: true,
    },
    {
      name: 'Serial',
      selector: 'serial_number',
      sortable: true,
      grow: 0.4,
    },
    {
      name: 'Servman Age',
      selector: 'age',
      sortable: true,
      grow: 0.4,
    },
    {
      name: 'Add to Request',
      selector: 'action',
      cell: (row) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSubmitAsset(row.id)}
          disabled={submittingAssetId === row.id || !submissionId}
        >
          {submittingAssetId === row.id ? 'Submitting...' : <Add />}
        </Button>
      ),
    },
  ];

  return (
    <LoadingWrapper isLoading={loading} message="Loading Customer Assets">
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary>
          <Typography variant="h5">Existing Assets</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ErrorMessage message={errorMessage} />
          <DataTable
            title=""
            columns={assetColumns}
            data={assets}
            striped
            dense
            highlightOnHover
            pointerOnHover
            pagination={false}
            noHeader
          />
          <ErrorMessage message={errorMessage} />
        </AccordionDetails>
      </Accordion>
    </LoadingWrapper>
  );
};

export default ExistingAssetsSection;
